import {ReactNode} from 'react';
import {view} from '@sail/ui/deprecated';
import {
  Conversation,
  InlineDataConversationMessage,
  Action,
  StripeAssistantCommandResponse,
} from '@stripe-internal/conversations-client';
import {InlineDataProps} from '@stripe-internal/inline-data';
import {defineMessages, FormattedMessage} from 'react-intl';
import {StripeAssistantRow} from '../../../Chat/Row/StripeAssistantRow';
import {InlineDataMessageLiveAnswersRow} from './InlineDataMessageLiveAnswersRow';
import {SupportSiteEscalation} from '../../../StripeAssistant/SupportSiteEscalation';
import {useAssistantAnalyticsCall} from '../../../StripeAssistant/analytics-utils';
import {useAnalytics} from '../../../AnalyticsProvider';
import {useConversationsLiveAnswersRow} from '../../../FeatureFlagsProvider';
import {LiveAnswersResponseContainer} from '../../../StripeAssistant/LiveAnswersV2/LiveAnswersResponseContainer';

type Props = {
  conversation: Conversation;
  message: InlineDataConversationMessage;
  children?: ReactNode;
  avatar?: ReactNode | null;
};

const messages = defineMessages({
  unanswerable: {
    id: 'conversations_ui.inline_data.assistant.unanswerable',
    description:
      'Message displayed when the assistant cannot answer a question',
    defaultMessage: "I couldn't answer that. Try another question.",
  },
  somethingWentWrong: {
    id: 'conversations_ui.inline_data.assistant.somethingWentWrong',
    description:
      'Message displayed when something goes wrong with the assistant',
    defaultMessage: 'Something went wrong. Please try again.',
  },
});

export const InlineDataStripeAssistantMessageRow = ({
  conversation,
  message,
}: Props) => {
  const analytics = useAnalytics();
  const inlineData = message.inlineData as InlineDataProps;

  const conversationsLiveAnswersRow = useConversationsLiveAnswersRow();

  useAssistantAnalyticsCall(conversation, message, (query: string | null) => {
    let classification = null;
    if (inlineData.name === 'live_answers_response') {
      classification = 'Summarize Documentation';
    } else if (
      inlineData.name === 'stripe_assistant' &&
      'classification' in inlineData.data &&
      !!inlineData.data.classification
    ) {
      classification = inlineData.data.classification;
    }

    if (classification) {
      analytics.action('ai_assistant_classify_intent_complete', {
        ai_assistant_question: query,
        ai_assistant_intent_classification: classification,
        ai_assistant_source: 'assistant',
      });
    }
  });

  // until we have a sanctioned path for stripe assistant messages,
  // we're overloading inline data messages. we shortcut here and
  // return an appropriate stripe assistant row
  if (inlineData.name !== 'stripe_assistant') {
    conversation.client.errors.reportError(
      'Rendered Assistant row with non-stripe_assistant inline data',
      {
        extras: {
          inlineDataName: inlineData.name,
          conversationId: conversation.id,
        },
      },
    );
    return null;
  }

  const type = 'type' in inlineData.data ? inlineData.data.type : null;

  switch (type) {
    case 'live_answers_v2':
      if (conversationsLiveAnswersRow) {
        return (
          <InlineDataMessageLiveAnswersRow
            conversation={conversation}
            message={message}
          />
        );
      }

      return (
        <LiveAnswersResponseContainer
          conversation={conversation}
          message={message}
        />
      );
    case 'analyze_data': {
      const requestAction = (action: Action) => {
        conversation.action({
          name: 'request-action',
          action,
        });
      };
      return (
        <view.div data-testid="stripe-assistant-message-row">
          <StripeAssistantRow
            conversation={conversation}
            message={message}
            requestAction={requestAction}
            stripeAssistantResponse={
              inlineData.data as unknown as StripeAssistantCommandResponse
            }
          />
        </view.div>
      );
    }

    case 'link_to_support':
      return (
        <view.div data-testid="stripe-assistant-message-row">
          <SupportSiteEscalation
            conversation={conversation}
            message={message}
          />
        </view.div>
      );

    case 'unanswerable':
      return (
        <view.div>
          <FormattedMessage {...messages.unanswerable} />
        </view.div>
      );

    default:
      return (
        <view.div>
          <FormattedMessage {...messages.somethingWentWrong} />
        </view.div>
      );
  }
};
